import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { $createParagraphNode, $insertNodes, LexicalEditor } from 'lexical';
import React, { useState } from 'react';
import { KeyValueMap } from '../../KeyValueMap';
import { ActionType } from '../../components/ActionType';
import ResponsiveToolbar from '../../components/ResponsiveToolbar';
import { FormContainer } from '../../form/FormContainer';
import FormItem from '../../form/FormItem';
import FormModal from '../../form/FormModal';
import FormWidget from '../../form/FormWidget';
import { FORM_WIDGET_COMPONENTS } from '../../form/formConstants';
import I18n from '../../i18n/I18n';
import { Icons } from '../../icons/Icons';
import { LayoutContainerNode } from '../LayoutContainerNode';
import { LayoutItemNode } from '../LayoutItemNode';
import { COLUMN_LAYOUTS, LAYOUT_MAP, LEXICAL_LAYOUT_TYPES } from '../lexicalConstants';

type LexicalInsertMenuProps = {
    editor: LexicalEditor;
    disabled?: boolean;
};

const LexicalInsertMenu = ({ disabled = false, editor }: LexicalInsertMenuProps) => {
    const [modalMode, setModalMode] = useState<string>(null);
    const [rows, setRows] = useState<number>(3);
    const [cols, setCols] = useState<number>(3);
    const [selectedLayout, setSelectedLayout] = useState<string | null>(null);

    const listTypes: KeyValueMap<string, ActionType> = {
        table: {
            icon: Icons.TableIcon,
            label: 'lexical.table'
        },
        tableLayout: {
            icon: Icons.ColumnIcon,
            label: 'lexical.columns.layout'
        }
    };

    const insertTable = () => {
        editor.dispatchCommand(INSERT_TABLE_COMMAND, {
            columns: String(cols),
            includeHeaders: true,
            rows: String(rows)
        });
    };

    const insertColumnsLayout = () => {
        editor.update(() => {
            const layoutMap: Record<string, string[]> = LAYOUT_MAP;
            const widths = layoutMap[selectedLayout ?? '2-equal'];
            const containerNode = new LayoutContainerNode(widths.join(' '));
            widths.forEach((width) => {
                const itemNode = new LayoutItemNode(
                    `width: ${width}; padding: 8px 16px; border: 1px dashed #ccc; box-sizing: border-box; margin: 5px 0px;`
                );
                itemNode.append($createParagraphNode());
                containerNode.append(itemNode);
            });

            $insertNodes([containerNode]);
        });
    };

    const saveModal = () => {
        if (modalMode === LEXICAL_LAYOUT_TYPES.table) {
            insertTable();
        } else if (modalMode === LEXICAL_LAYOUT_TYPES.columns) {
            insertColumnsLayout();
            setSelectedLayout(null);
        }
        setModalMode(null);
    };

    const handleSelect = (type: string) => {
        editor.focus();
        if (type === LEXICAL_LAYOUT_TYPES.table) {
            setModalMode(LEXICAL_LAYOUT_TYPES.table);
            return;
        }
        if (type === LEXICAL_LAYOUT_TYPES.tableLayout) {
            setModalMode(LEXICAL_LAYOUT_TYPES.columns);
        }
    };

    return (
        <>
            <ResponsiveToolbar
                disabled={disabled}
                isSmall
                selected={undefined}
                onSelect={handleSelect}
                options={listTypes}
                label={<I18n token="common.insert" />}
                multi={false}
            />

            <FormModal
                id="insert-modal"
                isOpen={modalMode !== null}
                title={
                    <I18n
                        token={
                            modalMode === LEXICAL_LAYOUT_TYPES.table
                                ? 'lexical.table.insertTable'
                                : 'lexical.columns.insertLayout'
                        }
                    />
                }
                onClose={() => setModalMode(null)}
                onSave={saveModal}
                sx={{ width: '340px', height: '260px' }}
                saveLabel={<I18n token="common.insert" />}
            >
                <FormContainer containerSx={{ padding: 1.5, justifyContent: 'center' }}>
                    <FormItem hidden={modalMode !== LEXICAL_LAYOUT_TYPES.table}>
                        <FormWidget
                            name="rows"
                            label={<I18n token="lexical.table.rows" />}
                            component={FORM_WIDGET_COMPONENTS.number}
                            value={rows}
                            onChange={(name, val) => setRows(Number(val))}
                        />
                    </FormItem>
                    <FormItem hidden={modalMode !== LEXICAL_LAYOUT_TYPES.table}>
                        <FormWidget
                            name="columns"
                            label={<I18n token="lexical.table.columns" />}
                            component={FORM_WIDGET_COMPONENTS.number}
                            value={cols}
                            onChange={(name, val) => setCols(Number(val))}
                        />
                    </FormItem>

                    <FormItem hidden={modalMode !== LEXICAL_LAYOUT_TYPES.columns}>
                        <FormWidget
                            name="layoutType"
                            label={<I18n token="lexical.columns.layoutType" />}
                            component={FORM_WIDGET_COMPONENTS.autocomplete}
                            options={COLUMN_LAYOUTS}
                            multiple={false}
                            value={selectedLayout}
                            onChange={(name, val) => setSelectedLayout(val)}
                        />
                    </FormItem>
                </FormContainer>
            </FormModal>
        </>
    );
};

export default LexicalInsertMenu;
